import React, { useState, useCallback, useRef } from 'react';
import { Sun, ArrowLeft, FileImage, X, Camera, Plus, Trash2, MapPin, Square } from 'lucide-react';
import Map, { NavigationControl } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { ElectricalPhase, EquipmentPreference, LocationData, MountingType, SurfaceType, UserType } from '../types';
import { Footer } from './Footer';
import { supabase } from '../lib/supabase';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiZ2FzYWJsZSIsImEiOiJjbHVqdzdzdHcwZHFqMmtuMnQ2cWdyMWJiIn0.pQdQv_mKaK165FmcpYyz-A';

const mountingAndSurfaceOptions: { type: string; label: string; description: string }[] = [
  {
    type: 'ground-mounted-dirt',
    label: 'Ground Mounted / Dirt',
    description: 'Ground installation on dirt/soil surface'
  },
  {
    type: 'rooftop-asphalt',
    label: 'Rooftop / Asphalt',
    description: 'Roof installation on asphalt surface'
  },
  {
    type: 'hanger',
    label: 'Hanger',
    description: 'Hanging installation system'
  },
  {
    type: 'carport-mounted',
    label: 'Carport-mounted (Canopy)',
    description: 'Canopy installation over parking areas'
  }
];

interface LocationSelectorProps {
  onNext: (data: LocationData) => void;
  onBack: () => void;
  initialData?: LocationData;
  currentProjectId?: string | null;
  user?: any;
  userType?: UserType | null;
}

export function LocationSelector({ onNext, onBack, initialData, currentProjectId, userType }: LocationSelectorProps) {
  // Legacy location state for backward compatibility
  const [location, setLocation] = useState({
    address: initialData?.address || '',
    latitude: initialData?.latitude || 24.7136,
    longitude: initialData?.longitude || 46.6753,
    areaInSqMeters: initialData?.areaInSqMeters || 0,
    tilt: initialData?.tilt || 10, // Default to 10 degrees for both industrial and agricultural workflows
    azimuth: initialData?.azimuth || 180, // Default to south-facing
    mountingType: initialData?.mountingType || 'roof-fixed',
    surfaceType: initialData?.surfaceType || 'asphalt',
    mountingAndSurface: initialData?.mountingAndSurface || 'rooftop-asphalt', // New combined field
    equipmentPreference: (initialData?.equipmentPreference || initialData?.locations?.[0]?.equipment_preference || 'median') as EquipmentPreference,
    gridPhase: (initialData?.electrical?.gridPhase ?? (userType === 'residential' ? 1 : 3)) as ElectricalPhase,
    acCableDistanceM: (initialData?.electrical?.acCableDistanceM ?? '') as number | '',
    dcCableDistanceM: (initialData?.electrical?.dcCableDistanceM ?? '') as number | '',
    photos: initialData?.photos,
    polygonData: initialData?.polygonData,
    agri_irrigation: initialData?.agri_irrigation
  });


  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<{
    blueprint?: File;
    location: File[];
  }>({
    location: []
  });

  const blueprintFileRef = useRef<HTMLInputElement>(null);
  const locationPhotosRef = useRef<HTMLInputElement>(null);


  const [viewport, setViewport] = useState({
    latitude: initialData?.latitude || 24.7136,
    longitude: initialData?.longitude || 46.6753,
    zoom: 18
  });

  const mapRef = useRef<any>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const [drawInstructions, setDrawInstructions] = useState(() => {
    const hasPolygonData = initialData?.polygonData && initialData.polygonData.length > 0;
    const hasLocations = initialData?.locations && initialData.locations.length > 0;
    const hasArea = initialData?.areaInSqMeters && initialData.areaInSqMeters > 0;
    return !hasArea && !hasPolygonData && !hasLocations;
  });
  
  // Multi-polygon state
  const [polygons, setPolygons] = useState<Array<{
    id: string;
    area: number;
    center: [number, number];
    address: string;
    feature: any;
    tilt: number;
    azimuth: number;
  }>>(() => {
    // Restore from polygonData (legacy) or locations (new format)
    if (initialData?.polygonData && initialData.polygonData.length > 0) {
      return initialData.polygonData.map((p: any) => ({
        id: p.id,
        area: p.area,
        center: p.center,
        address: p.address,
        feature: p.feature,
        tilt: p.tilt || 10, // Use saved tilt or default to 10
        azimuth: p.azimuth || 180 // Use saved azimuth or default to 180
      }));
    } else if (initialData?.locations && initialData.locations.length > 0) {
      return initialData.locations.map((loc: any) => ({
        id: loc.id,
        area: loc.area_sqm,
        center: [loc.longitude, loc.latitude],
        address: loc.address,
        feature: null, // Will be recreated when map loads
        tilt: loc.tilt || 10, // Use saved tilt or default to 10
        azimuth: loc.azimuth || 180 // Use saved azimuth or default to 180
      }));
    }
    return [];
  });
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null);

  // File upload handlers
  const handleFileUpload = (type: 'blueprint' | 'location', files: FileList | null) => {
    if (!files || files.length === 0) return;

    switch (type) {
      case 'blueprint':
        setSelectedFiles(prev => ({ ...prev, blueprint: files[0] }));
        break;
      case 'location':
        setSelectedFiles(prev => ({
          ...prev,
          [type]: [...prev[type], ...Array.from(files)]
        }));
        break;
    }
  };

  const removeFile = (type: 'blueprint' | 'location', index?: number) => {
    if (type === 'location' && typeof index === 'number') {
      setSelectedFiles(prev => ({
        ...prev,
        [type]: prev[type].filter((_, i) => i !== index)
      }));
    } else {
      setSelectedFiles(prev => ({
        ...prev,
        [type]: undefined
      }));
    }
  };

  const triggerFileInput = (type: 'blueprint' | 'location') => {
    switch (type) {
      case 'blueprint':
        blueprintFileRef.current?.click();
        break;
      case 'location':
        locationPhotosRef.current?.click();
        break;
    }
  };


  const initializeDraw = useCallback(() => {
    if (!mapRef.current || drawRef.current) return;

    const map = mapRef.current.getMap();
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true
      },
      defaultMode: 'draw_polygon',
      styles: [
        {
          id: 'gl-draw-line',
          type: 'line',
          filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#3b82f6',
            'line-dasharray': [0.2, 2],
            'line-width': 2
          }
        },
        {
          id: 'gl-draw-polygon-fill',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: {
            'fill-color': '#3b82f6',
            'fill-outline-color': '#3b82f6',
            'fill-opacity': 0.2
          }
        },
        {
          id: 'gl-draw-polygon-stroke',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 3
          }
        },
        {
          id: 'gl-draw-polygon-and-line-vertex-active',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
          paint: {
            'circle-radius': 8,
            'circle-color': '#fff',
            'circle-stroke-color': '#3b82f6',
            'circle-stroke-width': 2
          }
        },
        {
          id: 'gl-draw-polygon-first-point',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['==', 'active', 'true']],
          paint: {
            'circle-radius': 10,
            'circle-color': '#fff',
            'circle-stroke-color': '#3b82f6',
            'circle-stroke-width': 3
          }
        },
        {
          id: 'gl-draw-polygon-midpoint',
          type: 'circle',
          filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
          paint: {
            'circle-radius': 5,
            'circle-color': '#3b82f6',
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 2
          }
        }
      ]
    });

    map.addControl(draw);
    drawRef.current = draw;

    // Start in draw mode
    draw.changeMode('draw_polygon');

    // Event handlers
    const handleDrawCreate = async (e: any) => {
      const feature = e.features[0];
      if (!feature || feature.geometry.type !== 'Polygon') return;

      const areaValue = turf.area(feature);
      const center = turf.center(feature);
      // Use the feature's actual ID from MapboxDraw
      const polygonId = feature.id || `polygon_${Date.now()}`;

      // Get address for the center point
      const address = await getAddressFromCoords(
        center.geometry.coordinates[1],
        center.geometry.coordinates[0]
      );

      const newPolygon = {
        id: polygonId,
        area: Math.round(areaValue),
        center: [center.geometry.coordinates[0], center.geometry.coordinates[1]] as [number, number],
        address: address || 'Address not found',
        feature: feature,
        tilt: 10, // Default tilt for new areas
        azimuth: 180 // Default azimuth for new areas
      };

      setPolygons(prev => [...prev, newPolygon]);

      // Update total area
      const totalArea = [...polygons, newPolygon].reduce((sum, poly) => sum + poly.area, 0);
      setLocation(prev => ({
        ...prev,
        areaInSqMeters: totalArea,
        latitude: center.geometry.coordinates[1],
        longitude: center.geometry.coordinates[0]
      }));

      setDrawInstructions(false);
      setIsDrawingMode(false);
    };

    const handleDrawUpdate = (e: any) => {
      const feature = e.features[0];
      if (!feature || feature.geometry.type !== 'Polygon') return;

      const areaValue = turf.area(feature);
      const center = turf.center(feature);

      setLocation(prev => ({
        ...prev,
        areaInSqMeters: Math.round(areaValue),
        latitude: center.geometry.coordinates[1],
        longitude: center.geometry.coordinates[0]
      }));
    };

    const handleDrawDelete = (e: any) => {
      if (e.features && e.features.length > 0) {
        const deletedIds = e.features.map((f: any) => f.id);
        setPolygons(prev => {
          const updated = prev.filter(poly => !deletedIds.includes(poly.id));
          const totalArea = updated.reduce((sum, poly) => sum + poly.area, 0);
          setLocation(prev => ({
            ...prev,
            areaInSqMeters: totalArea
          }));
          return updated;
        });
      } else {
        // Clear all polygons
        setPolygons([]);
      setLocation(prev => ({
        ...prev,
        areaInSqMeters: 0
      }));
      }
      
      if (polygons.length <= 1) {
      setDrawInstructions(true);
      }
    };

    const handleDrawModeChange = (e: any) => {
      setIsDrawingMode(e.mode === 'draw_polygon');
      if (e.mode === 'simple_select' && draw.getAll().features.length === 0) {
        draw.changeMode('draw_polygon');
      }
    };

    const handleDrawRender = () => {
      if (draw.getAll().features.length === 0) {
        setDrawInstructions(true);
      }
    };

    // Add event listeners
    map.on('draw.create', handleDrawCreate);
    map.on('draw.update', handleDrawUpdate);
    map.on('draw.delete', handleDrawDelete);
    map.on('draw.modechange', handleDrawModeChange);
    map.on('draw.render', handleDrawRender);

    return () => {
      map.off('draw.create', handleDrawCreate);
      map.off('draw.update', handleDrawUpdate);
      map.off('draw.delete', handleDrawDelete);
      map.off('draw.modechange', handleDrawModeChange);
      map.off('draw.render', handleDrawRender);
      map.removeControl(draw);
    };
  }, [polygons]);

  // New functions for multi-polygon management
  const startDrawing = () => {
    if (drawRef.current) {
      drawRef.current.changeMode('draw_polygon');
      setIsDrawingMode(true);
      setDrawInstructions(false); // Hide instructions when user starts drawing
    }
  };

  const deletePolygon = (polygonId: string) => {
    if (drawRef.current && mapRef.current) {
      try {
        // Get the map instance
        const map = mapRef.current.getMap();
        
        // Remove from map using the correct MapboxDraw API
        (drawRef.current as any).delete(polygonId);
        
        // Force map to re-render to ensure the polygon is removed
        map.triggerRepaint();
        
        // Remove from our state
        setPolygons(prev => {
          const updated = prev.filter(poly => poly.id !== polygonId);
          const totalArea = updated.reduce((sum, poly) => sum + poly.area, 0);
          setLocation(prev => ({
            ...prev,
            areaInSqMeters: totalArea
          }));
          return updated;
        });
        
        // Clear selection if this was the selected polygon
        if (selectedPolygonId === polygonId) {
          setSelectedPolygonId(null);
        }
      } catch (error) {
        console.warn('Could not remove feature from map:', polygonId, error);
        // Fallback: just remove from state if map removal fails
        setPolygons(prev => {
          const updated = prev.filter(poly => poly.id !== polygonId);
          const totalArea = updated.reduce((sum, poly) => sum + poly.area, 0);
          setLocation(prev => ({
            ...prev,
            areaInSqMeters: totalArea
          }));
          return updated;
        });
      }
    }
  };

  const clearAllPolygons = () => {
    if (drawRef.current && mapRef.current) {
      try {
        // Get the map instance
        const map = mapRef.current.getMap();
        
        // Clear from map first using the correct MapboxDraw API
        (drawRef.current as any).deleteAll();
        
        // Force map to re-render to ensure all polygons are removed
        map.triggerRepaint();
        
        // Clear from our state
        setPolygons([]);
        setLocation(prev => ({
          ...prev,
          areaInSqMeters: 0
        }));
        
        // Reset drawing instructions
        setDrawInstructions(true);
        setSelectedPolygonId(null);
      } catch (error) {
        console.warn('Could not clear all features from map:', error);
        // Fallback: remove each feature individually
        const features = drawRef.current.getAll().features;
        features.forEach((feature: any) => {
          try {
            (drawRef.current as any)?.delete(feature.id);
          } catch (e) {
            console.warn('Could not remove feature:', feature.id);
          }
        });
        
        // Clear from our state even if map operations fail
        setPolygons([]);
        setLocation(prev => ({
          ...prev,
          areaInSqMeters: 0
        }));
        setDrawInstructions(true);
        setSelectedPolygonId(null);
      }
    }
  };

  const selectPolygon = (polygonId: string) => {
    setSelectedPolygonId(polygonId);
    if (drawRef.current) {
      const features = drawRef.current.getAll().features;
      const feature = features.find((f: any) => f.id === polygonId);
      if (feature) {
        drawRef.current.changeMode('simple_select');
        // Select the specific feature
        drawRef.current.changeMode('simple_select');
      }
    }
  };

  const getAddressFromCoords = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
      );
      const data = await response.json();
      return data.features?.[0]?.place_name || '';
    } catch (error) {
      console.error('Error getting address:', error);
      return '';
    }
  };

  const handleMapLoad = () => {
    initializeDraw();
    
    // Auto-hide draw instructions on user interaction
    const hideInstructions = () => {
      setDrawInstructions(false);
    };
    
    // Add event listeners to hide instructions on user interaction
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      
      // Hide on map click
      map.on('click', hideInstructions);
      
      // Hide on draw start
      map.on('draw.create', hideInstructions);
      map.on('draw.start', hideInstructions);
      
      // Hide on navigation (zoom, pan)
      map.on('zoomstart', hideInstructions);
      map.on('movestart', hideInstructions);
      
      // Hide on any user interaction with map controls
      map.on('touchstart', hideInstructions);
      map.on('mousedown', hideInstructions);
    }
    
    // Restore polygons from initial data
    if (initialData?.polygonData && initialData.polygonData.length > 0 && drawRef.current) {
      // Legacy format: Add each polygon back to the map
      initialData.polygonData.forEach((polygonData: any) => {
        if (polygonData.feature) {
          try {
            drawRef.current?.add(polygonData.feature);
          } catch (error) {
            console.warn('Could not restore polygon:', polygonData.id);
          }
        }
      });
      setDrawInstructions(false);
    } else if (initialData?.locations && initialData.locations.length > 0 && drawRef.current) {
      // New format: Don't create circles - the polygons should already be in state
      // The polygons state was initialized from locations data in the useState
      // We just need to add them back to the map if they have features
      polygons.forEach((polygon) => {
        if (polygon.feature) {
          try {
            drawRef.current?.add(polygon.feature);
          } catch (error) {
            console.warn('Could not restore polygon feature:', polygon.id);
          }
        }
      });
      setDrawInstructions(false);
    } else if (initialData?.areaInSqMeters && initialData.areaInSqMeters > 0 && drawRef.current && initialData.longitude && initialData.latitude) {
      // Fallback: Create a simple polygon around the center point for legacy data
      const center: [number, number] = [initialData.longitude, initialData.latitude];
      const radius = Math.sqrt(initialData.areaInSqMeters / Math.PI); // Approximate radius in meters
      const options = { steps: 64, units: 'meters' as const };
      const circle = turf.circle(center, radius / 1000, options);
      
      // Add the feature to the map
      drawRef.current.add(circle);
      setDrawInstructions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (polygons.length === 0) {
      alert('Please draw at least one installation area on the map');
      return;
    }

    // Validate individual polygon settings
    for (let i = 0; i < polygons.length; i++) {
      const polygon = polygons[i];
      if (!polygon.tilt || polygon.tilt === 0) {
        alert(`Please enter the surface tilt angle for Area ${i + 1}`);
        return;
      }
      if (!polygon.azimuth || polygon.azimuth === 0) {
        alert(`Please enter the azimuth angle for Area ${i + 1}`);
      return;
      }
    }

    if (location.acCableDistanceM !== '' && location.acCableDistanceM <= 0) {
      alert('Please enter a valid AC cable distance from MDB → inverter (meters)');
      return;
    }

    if (location.dcCableDistanceM !== '' && location.dcCableDistanceM <= 0) {
      alert('Please enter a valid DC cable distance from PV field → inverter (meters)');
      return;
    }

    // Create location entries for each polygon
    const locationEntries = polygons.map((polygon) => ({
      id: polygon.id,
      address: polygon.address,
      latitude: polygon.center[1],
      longitude: polygon.center[0],
      area_sqm: polygon.area,
      tilt: polygon.tilt,
      azimuth: polygon.azimuth,
      mounting_type: location.mountingType as MountingType,
      surface_type: location.surfaceType as SurfaceType,
      mounting_and_surface: location.mountingAndSurface || 'rooftop-asphalt',
      equipment_preference: location.equipmentPreference
    }));

    // Calculate total area from all locations
    const totalArea = locationEntries.reduce((sum, entry) => sum + entry.area_sqm, 0);

    // Clean location data structure - no redundant global fields
    const locationData: LocationData = {
      locations: locationEntries,
      photos: selectedFiles.location,
      total_area_sqm: totalArea,
      electrical: {
        gridPhase: location.gridPhase,
        acCableDistanceM: location.acCableDistanceM === '' ? undefined : location.acCableDistanceM,
        dcCableDistanceM: location.dcCableDistanceM === '' ? undefined : location.dcCableDistanceM
      }
    };

    // Save to database if project ID exists
    if (currentProjectId) {
      try {
        let photoUrls: string[] = [];
        
        // Upload location photos to location_photos bucket
        if (selectedFiles.location && selectedFiles.location.length > 0) {
          const uploadPromises = selectedFiles.location.map(async (photo: File, index: number) => {
            const fileName = `location_photos/${currentProjectId}/${Date.now()}-${index}-${photo.name}`;
            
            const { error: uploadError } = await supabase.storage
              .from('location_photos')
              .upload(fileName, photo);

            if (uploadError) {
              console.error('Error uploading photo:', uploadError);
              return null;
            }

            const { data: urlData } = supabase.storage
              .from('location_photos')
              .getPublicUrl(fileName);

            return urlData.publicUrl;
          });

          const uploadedUrls = await Promise.all(uploadPromises);
          photoUrls = uploadedUrls.filter((url: string | null) => url !== null);
        }

        const updateData: any = {
          location_data: locationData,
          updated_at: new Date().toISOString()
        };

        // Add location photos URLs if any were uploaded
        if (photoUrls.length > 0) {
          updateData.location_photos = photoUrls;
        }

        const { error: updateError } = await supabase
          .from('projects')
          .update(updateData)
          .eq('id', currentProjectId);

        if (updateError) {
          console.error('Error updating project with location data:', updateError);
          alert('Failed to save location data. Please try again.');
          return;
        }
      } catch (error) {
        console.error('Database error:', error);
        alert('Failed to save location data. Please try again.');
        return;
      }
    }

    onNext(locationData);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200">
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Design Requirements</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">



          {/* Map Controls */}
          <div className="mb-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={startDrawing}
              className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all ${
                isDrawingMode 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              <Plus className="h-4 w-4 mr-2" />
              {isDrawingMode ? 'Drawing Mode Active' : 'Add New Area'}
            </button>
            
            {polygons.length > 0 && (
              <button
                type="button"
                onClick={clearAllPolygons}
                className="flex items-center px-4 py-2 rounded-lg font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-all"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All Areas
              </button>
            )}
            
            <div className="flex items-center text-sm text-gray-600">
              <Square className="h-4 w-4 mr-1" />
              {polygons.length} area{polygons.length !== 1 ? 's' : ''} selected
            </div>
          </div>

          <div className="relative">
            <div className="h-[300px] sm:h-[400px] rounded-lg overflow-hidden">
              <Map
                {...viewport}
                onMove={evt => setViewport(evt.viewState)}
                style={{ width: '100%', height: '100%' }}
                mapStyle="mapbox://styles/mapbox/satellite-v9"
                mapboxAccessToken={MAPBOX_TOKEN}
                ref={mapRef}
                onLoad={handleMapLoad}
              >
                <NavigationControl position="top-right" />
              </Map>
            </div>

            {drawInstructions && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg text-center max-w-xs">
                  <p className="text-gray-800 font-medium">
                    Click "Add New Area" to start drawing your first installation zone
                  </p>
                  <p className="text-gray-600 text-sm mt-2">
                    You can add multiple areas for different zones
                  </p>
                </div>
              </div>
            )}

            {isDrawingMode && (
              <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                  Drawing Mode - Click to start new polygon
                </div>
              </div>
            )}
          </div>

          {/* Areas Table */}
          {polygons.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                  Installation Areas ({polygons.length})
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Area
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Area (m²)
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Surface Tilt
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Azimuth
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {polygons.map((polygon, index) => (
                      <tr 
                        key={polygon.id}
                        className={`hover:bg-blue-50/30 transition-all duration-200 ${
                          selectedPolygonId === polygon.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                        }`}
                        onClick={() => selectPolygon(polygon.id)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
                              <span className="text-sm font-bold text-white">
                                {index + 1}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate font-medium" title={polygon.address}>
                            {polygon.address}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-semibold bg-gray-100 px-3 py-1 rounded-full inline-block">
                            {polygon.area.toLocaleString()} m²
            </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max="90"
                              value={polygon.tilt}
                              onChange={(e) => {
                                const newTilt = Number(e.target.value);
                                // Validate tilt: 0-90 degrees
                                if (newTilt >= 0 && newTilt <= 90) {
                                  setPolygons(prev => prev.map(p => 
                                    p.id === polygon.id ? { ...p, tilt: newTilt } : p
                                  ));
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-20 px-3 py-1 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                            />
                            <span className="text-sm text-gray-500">°</span>
              </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max="360"
                              value={polygon.azimuth}
                              onChange={(e) => {
                                const newAzimuth = Number(e.target.value);
                                // Validate azimuth: 0-360 degrees
                                if (newAzimuth >= 0 && newAzimuth <= 360) {
                                  setPolygons(prev => prev.map(p => 
                                    p.id === polygon.id ? { ...p, azimuth: newAzimuth } : p
                                  ));
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-20 px-3 py-1 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                            />
                            <span className="text-sm text-gray-500">°</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              deletePolygon(polygon.id);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all duration-200"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          <div className="border-t pt-8">
            <label className="block text-xl font-semibold text-gray-900 mb-6">
              Mounting Type & Surface
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {mountingAndSurfaceOptions.map((option) => (
                <div
                  key={option.type}
                  className={`group relative rounded-xl border-2 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg ${
                    location.mountingAndSurface === option.type
                      ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-md'
                      : 'border-gray-200 hover:border-blue-300 bg-white hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    // Map the combined option to individual mountingType and surfaceType
                    let mountingType: string = 'roof-fixed';
                    let surfaceType: string = 'asphalt';
                    
                    switch (option.type) {
                      case 'ground-mounted-dirt':
                        mountingType = 'ground-fixed';
                        surfaceType = 'soil';
                        break;
                      case 'rooftop-asphalt':
                        mountingType = 'roof-fixed';
                        surfaceType = 'asphalt';
                        break;
                      case 'hanger':
                        mountingType = 'roof-fixed';
                        surfaceType = 'metal';
                        break;
                      case 'carport-mounted':
                        mountingType = 'carport-mounted';
                        surfaceType = 'concrete';
                        break;
                    }
                    
                    setLocation({ 
                      ...location, 
                      mountingAndSurface: option.type,
                      mountingType: mountingType as any,
                      surfaceType: surfaceType as any
                    });
                  }}
                >
                  {/* Selection indicator */}
                  {location.mountingAndSurface === option.type && (
                    <div className="absolute top-4 right-4">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col h-full">
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                      location.mountingAndSurface === option.type
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600'
                    }`}>
                      <Sun className="h-6 w-6" />
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-2 transition-colors ${
                        location.mountingAndSurface === option.type
                          ? 'text-blue-900'
                          : 'text-gray-900 group-hover:text-blue-900'
                      }`}>
                        {option.label}
                      </h3>
                      <p className={`text-sm leading-relaxed ${
                        location.mountingAndSurface === option.type
                          ? 'text-blue-700'
                          : 'text-gray-600 group-hover:text-blue-700'
                      }`}>
                        {option.description}
                      </p>

                      {/* Engineering Note - Show only on selected option */}
                      {location.mountingAndSurface === option.type && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg className="h-4 w-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-2">
                              <p className="text-xs font-medium text-amber-800">
                                Engineering analysis may be required
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Hover effect overlay */}
                    <div className={`absolute inset-0 rounded-xl transition-opacity duration-200 ${
                      location.mountingAndSurface === option.type
                        ? 'opacity-0'
                        : 'opacity-0 group-hover:opacity-5 bg-blue-500'
                    }`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Equipment preference (applies to all workflows) */}
            <div className="mt-8">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Equipment Preference
              </label>
              <p className="text-sm text-gray-600 mb-4">
                Choose how we select the module & inverter from our equipment database.
              </p>
              <div className="flex flex-wrap gap-2">
                {([
                  { key: 'price', label: 'Lowest Price' },
                  { key: 'median', label: 'Median' },
                  { key: 'quality', label: 'Highest Quality' },
                ] as Array<{ key: EquipmentPreference; label: string }>).map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setLocation(prev => ({ ...prev, equipmentPreference: opt.key }))}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      location.equipmentPreference === opt.key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 text-xs text-gray-500">
                This choice will be used for residential, corporate/commercial, industrial, and agricultural workflows.
              </div>
            </div>

            {/* Electrical (for IEC cable sizing + BOQ) */}
            <div className="mt-10">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Electrical (for cable sizing)
              </label>
              <p className="text-sm text-gray-600 mb-4">
                Used for IEC (60364-5-52) cable sizing: copper only, multicore cable, PF=0.8.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grid phase
                  </label>
                  <select
                    value={location.gridPhase}
                    onChange={(e) => {
                      const phase = Number(e.target.value) as ElectricalPhase;
                      setLocation(prev => ({ ...prev, gridPhase: phase }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={1}>Single phase (230V)</option>
                    <option value={3}>Three phase (415V)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AC distance (MDB → inverter) (m)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={location.acCableDistanceM}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setLocation(prev => ({
                        ...prev,
                        acCableDistanceM: raw === '' ? '' : Number(raw)
                      }));
                    }}
                    placeholder="e.g. 50"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DC distance (PV → inverter) (m)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={location.dcCableDistanceM}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setLocation(prev => ({
                        ...prev,
                        dcCableDistanceM: raw === '' ? '' : Number(raw)
                      }));
                    }}
                    placeholder="e.g. 80"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-500">
                Voltage is automatically set to {location.gridPhase === 1 ? '230V' : '415V'} based on phase. Leave distances blank to use default BOQ lengths.
              </div>
            </div>
          </div>

          {/* Installation Location Photos */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Camera className="h-5 w-5 mr-2 text-blue-600" />
              Installation Location Photos
            </h3>
            
            {selectedFiles.location.length > 0 && (
              <div className="mb-4 space-y-2">
                {selectedFiles.location.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <FileImage className="h-5 w-5 text-green-600 mr-2" />
                      <span className="text-green-700">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile('location', index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div 
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => triggerFileInput('location')}
            >
              <FileImage className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                Upload photos of the installation location
              </p>
              <p className="text-xs text-gray-500 mt-1">
                (Roof, ground area, or installation site)
              </p>
              <input
                ref={locationPhotosRef}
                type="file"
                multiple
                className="hidden"
                accept="image/*"
                onChange={(e) => handleFileUpload('location', e.target.files)}
              />
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-start">
              <Sun className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Why these details matter
                </h3>
                <p className="mt-2 text-sm text-blue-700">
                  These specifications help us design the most efficient energy system for your location. 
                  The mounting type, surface material, and orientation all affect energy production and system performance.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={onBack}
              className="bg-gray-100 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg"
            >
              Back
            </button>
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg shadow-lg"
            >
              Generate Energy Design
            </button>
          </div>
        </form>
      </div>
      
      <div className="mt-12">
        <Footer />
      </div>
    </div>
  );
}